import React, { useState, SyntheticEvent, useCallback } from 'react';
import { Modal, Button, Descriptions, Tag, Input, Tooltip, Menu } from 'antd';
import { setFileAnnotations, deleteInference } from '../../../lib/api/aws-api';
import { createTraining } from '../../../lib/api/training-api';
import UploadContainer from '../upload/UploadContainer';
import { Model, InferenceFile, Training, ModelVersion } from '../../../types';
import { BlockedDiv } from '../../../components/BlockedDiv';
import { FormItemLabel, ModalSubTitle, ModalTitle } from '../../../components/text/Titles';

type Props = {
  visible: boolean;
  onClose: () => any;
  onCreate: (training: Training) => any;
  model: Model;
  version: null | ModelVersion;
};

const CreateClassificationVersion = (props: Props) => {
  const { visible, onClose, onCreate, model, version } = props;

  const classes = (model && model.info && model.info.annotationTypes) || [];

  const [selectedClass, setSelectedClass] = useState<null | string>(classes.length > 0 ? classes[0] : null);
  const [inferenceMap, setInferenceMap] = useState<{
    [className: string]: InferenceFile[];
  }>({});
  const [versionValue, setVersionValue] = useState<string>('');
  const [step, setStep] = useState<0 | 1>(0);

  const goToUploadEnabled = versionValue && versionValue.length > 0;

  const submitEnabled = () => {
    if (inferenceMap[classes[0]] && inferenceMap[classes[0]].length === 0) return false;

    const equalUploadsPerCase = classes.every(
      className => inferenceMap[className] && inferenceMap[className].length === inferenceMap[classes[0]].length
    );
    if (!equalUploadsPerCase) return false;
    const uploadsCompleted = getInferenceList().every(inference => ['FINISHED', 'ERROR'].includes(inference.status));
    if (!uploadsCompleted) return false;
    return true;
  };

  const getInferenceList = useCallback(() => {
    return Object.values(inferenceMap).reduce((list, values) => [...list, ...values], []);
  }, [inferenceMap]);
  const rollbackCreation = useCallback(async () => {
    setInferenceMap({});
    try {
      await Promise.all(getInferenceList().map(inference => deleteInference(inference.id)));
    } catch (e) {
      console.log(e);
    }
  }, [setInferenceMap, getInferenceList]);

  const onVersionChange = (event: SyntheticEvent<HTMLInputElement>) => {
    setVersionValue(event.currentTarget.value);
  };

  const goToUpload = () => {
    setStep(1);
  };

  const onSubmit = async () => {
    const training = await createTraining(
      model.id,
      getInferenceList().map(inference => inference.id),
      versionValue
    );

    // modify each inference with selected class
    await Promise.all(
      Object.keys(inferenceMap).map(key =>
        Promise.all(
          inferenceMap[key].map(inference =>
            setFileAnnotations(inference.id, {
              type: {
                reliable: true,
                name: key,
              },
              fields: [],
            })
          )
        )
      )
    );

    onModalClose();
    onCreate(training);
  };

  const onModalClose = async (remove?: boolean) => {
    if (remove) {
      await rollbackCreation();
    }

    setVersionValue('');
    setStep(0);

    onClose();
  };

  const onInferenceListUpdate = (inferenceList: InferenceFile[], className: string) => {
    setInferenceMap({ ...inferenceMap, [className]: inferenceList });
  };

  const getInferencesStatusColor = (className: string) => {
    const inferenceList = inferenceMap[className] || [];
    if (inferenceList.some(inference => inference.status === 'ERROR')) {
      return '#fa541c';
    } else if (inferenceList.some(inference => inference.status === 'PROCESSING')) {
      return 'orange';
    } else {
      return '#52c41a';
    }
  };

  return (
    <Modal
      maskClosable={false}
      visible={visible}
      title={<ModalTitle>Train new version</ModalTitle>}
      onCancel={() => onModalClose(true)}
      footer={
        step === 0 ? (
          <div>
            <Button type="default" onClick={() => onModalClose(true)}>
              Cancel
            </Button>
            <Tooltip title={goToUploadEnabled ? '' : 'Enter a version name before continuing.'}>
              <Button style={{ marginLeft: '10px' }} type="primary" onClick={goToUpload} disabled={!goToUploadEnabled}>
                Next
              </Button>
            </Tooltip>
          </div>
        ) : (
          <div>
            <Button type="default" onClick={() => onModalClose(true)}>
              Cancel
            </Button>
            <Tooltip title={submitEnabled() ? '' : 'Upload at least 1 document and wait for them to finish analysing.'}>
              <Button type="primary" onClick={onSubmit} style={{ marginLeft: '10px' }} disabled={!submitEnabled()}>
                Review
              </Button>
            </Tooltip>
          </div>
        )
      }>
      {step === 0 ? (
        <div>
          <FormItemLabel>This process will upgrade an existing model.</FormItemLabel>
          <ModalSubTitle>Current version</ModalSubTitle>
          <BlockedDiv alteredPadding>
            <Descriptions layout="horizontal" column={1}>
              <Descriptions.Item label="Model name">{model.name}</Descriptions.Item>
              <Descriptions.Item label="Version">{version && version.modelVersionName}</Descriptions.Item>
              <Descriptions.Item label="Classes">
                {classes.map((className, index) => {
                  return (
                    <Tag
                      key={index}
                      style={{
                        padding: '5px',
                        marginTop: '10px',
                        backgroundColor: '#ffffff',
                        borderColor: '#e8e8e8',
                      }}>
                      <span
                        style={{
                          color: '#6d6e77',
                          margin: '5px',
                          fontSize: '12px',
                        }}>
                        {className}
                      </span>
                    </Tag>
                  );
                })}
              </Descriptions.Item>
            </Descriptions>
          </BlockedDiv>

          <ModalSubTitle>New version</ModalSubTitle>
          <Input
            value={versionValue}
            placeholder="Version"
            onChange={onVersionChange}
            style={{ margin: '10px 0px 10px 0px' }}
          />
        </div>
      ) : (
        <div>
          <FormItemLabel>
            We’ll be needing some example documents for each class to make an analysis. We advise you to upload up a
            minimum of 3 documents.
          </FormItemLabel>
          <Menu
            onClick={e => setSelectedClass(e.key)}
            selectedKeys={[selectedClass || '']}
            mode="horizontal"
            style={{ marginBottom: 20 }}>
            {classes.map(className => (
              <Menu.Item key={className}>
                {className} (
                <span style={{ color: getInferencesStatusColor(className) }}>
                  {(inferenceMap[className] || []).length}
                </span>
                )
              </Menu.Item>
            ))}
          </Menu>
          {classes.map(className => (
            <UploadContainer
              hide={className !== selectedClass}
              modelId={model.id}
              inferenceList={inferenceMap[className] || []}
              onInferenceListUpdated={inferenceList => onInferenceListUpdate(inferenceList, className)}
            />
          ))}
        </div>
      )}
    </Modal>
  );
};

export default CreateClassificationVersion;
